import { toKebab } from './bi-fields';

// A widget's BI message from the dashboard runtime: bridge-shim `sym.bi`, `kind: 'bi'`. The runtime sends it
// itself (an impression when the card is on screen, a click on every action press), so no widget calls it.
export type WidgetBiMessage = {
  event?: string;
  widget?: string;
  action?: string;
  fields?: Record<string, unknown>;
};

export type WidgetBiSurface = { surface: 'home' | 'space' | 'page'; space?: string; page?: string };

export type WidgetBiEvent = {
  type: 'itemSeen' | 'clickCta';
  componentName: string;
  additionalFields: Record<string, string | undefined>;
};

const MAX_FIELDS = 20;

/**
 * The generic event a widget's `kind: 'bi'` message becomes: `itemSeen` (198:6) or `clickCta` (198:10), with
 * the widget slug as `componentName`. Null for anything else, so an unknown event is dropped, not logged.
 *
 * The widget's own fields keep primitives only (a widget runs untrusted markup, so nothing structured is
 * forwarded), and the HOST's context is written last: a widget can never overwrite where it was shown or
 * which action ran.
 */
export function widgetBiEvent(d: WidgetBiMessage, where: WidgetBiSurface): WidgetBiEvent | null {
  if (d.event !== 'itemSeen' && d.event !== 'clickCta') return null;
  const componentName = toKebab(d.widget);
  if (!componentName) return null;
  const additionalFields: Record<string, string | undefined> = {};
  const fields = d.fields && typeof d.fields === 'object' ? d.fields : {};
  for (const [key, value] of Object.entries(fields).slice(0, MAX_FIELDS)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      additionalFields[key.slice(0, 50)] = String(value).slice(0, 200);
    }
  }
  additionalFields.surface = where.surface;
  additionalFields.space = where.space;
  additionalFields.page = where.page;
  additionalFields.action = d.event === 'clickCta' && d.action ? String(d.action).slice(0, 100) : undefined;
  return { type: d.event, componentName, additionalFields };
}
